import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Download, TrendingUp, Calculator, Building2, Car } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { exportToExcel } from "@/utils/excelExport";
import { HST_RATE, HST_DIVISOR } from "@/types/crm";
import { calculateHST, calculateNetPrice } from "@/utils/financials";

type Vehicle = Tables<"vehicles">;
type VehicleExpense = Tables<"vehicle_expenses">;
type BusinessExpense = Tables<"business_expenses">;

export default function FinancialDashboard() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleExpenses, setVehicleExpenses] = useState<VehicleExpense[]>([]);
  const [businessExpenses, setBusinessExpenses] = useState<BusinessExpense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const handleCustomStartChange = (val: string) => {
    const todayStr = new Date().toISOString().split("T")[0];
    if (val && val > todayStr) {
      toast.error("Start date cannot be in the future");
      return;
    }
    if (val && customEnd && val > customEnd) {
      toast.error("Start date cannot be after end date");
      return;
    }
    setCustomStart(val);
  };

  const handleCustomEndChange = (val: string) => {
    const todayStr = new Date().toISOString().split("T")[0];
    if (val && val > todayStr) {
      toast.error("End date cannot be in the future");
      return;
    }
    if (val && customStart && val < customStart) {
      toast.error("End date cannot be before start date");
      return;
    }
    setCustomEnd(val);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    const [vRes, veRes, beRes] = await Promise.all([
      supabase.from("vehicles").select("id, year, make, model, vin, status, purchase_cost, purchase_hst, purchase_date, sold_price, sold_date, hst_collected, trade_in_value"),
      supabase.from("vehicle_expenses").select("id, vehicle_id, category, description, amount, hst_amount, expense_date, receipt_filename").is("deleted_at", null),
      supabase.from("business_expenses").select("id, category, description, amount, hst_amount, expense_date, receipt_filename").is("deleted_at", null),
    ]);
    setVehicles(vRes.data || []);
    setVehicleExpenses(veRes.data || []);
    setBusinessExpenses(beRes.data || []);
    setIsLoading(false);
  };

  // Period filtering — enhanced with specific date/month/quarter/year pickers
  const getDateRange = (): { start: Date; end: Date } | null => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const quarter = Math.floor(month / 3);

    switch (period) {
      case "month":
        return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
      case "last_month": {
        const lm = month === 0 ? 11 : month - 1;
        const ly = month === 0 ? year - 1 : year;
        return { start: new Date(ly, lm, 1), end: new Date(ly, lm + 1, 0) };
      }
      case "quarter":
        return { start: new Date(year, quarter * 3, 1), end: new Date(year, (quarter + 1) * 3, 0) };
      case "last_quarter": {
        const lq = quarter === 0 ? 3 : quarter - 1;
        const lqy = quarter === 0 ? year - 1 : year;
        return { start: new Date(lqy, lq * 3, 1), end: new Date(lqy, (lq + 1) * 3, 0) };
      }
      case "year":
        return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
      case "last_year":
        return { start: new Date(year - 1, 0, 1), end: new Date(year - 1, 11, 31) };
      case "custom":
        if (customStart && customEnd) {
          return { start: new Date(customStart + "T00:00:00"), end: new Date(customEnd + "T23:59:59") };
        }
        return null;
      default:
        return null;
    }
  };

  const parseLocalDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    try {
      const baseStr = dateStr.split("T")[0];
      const parts = baseStr.split("-");
      if (parts.length !== 3) {
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // 0-indexed
      const day = parseInt(parts[2], 10);
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      if (month < 0 || month > 11 || day < 1 || day > 31) return null;
      const date = new Date(year, month, day);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const isInPeriod = (dateStr: string) => {
    const range = getDateRange();
    if (!range) return true;
    const date = parseLocalDate(dateStr);
    if (!date) return false;
    return date >= range.start && date <= range.end;
  };

  // Filtered data
  const soldVehicles = vehicles.filter((v) => v.status === "sold" && v.sold_price && (!getDateRange() || (v.sold_date && isInPeriod(v.sold_date))));
  const filteredVehicleExpenses = vehicleExpenses.filter((e) => isInPeriod(e.expense_date));
  const filteredBusinessExpenses = businessExpenses.filter((e) => isInPeriod(e.expense_date));

  const hstCollectedTotal = soldVehicles.reduce((sum, v) => {
    const manualHst = Number(v.hst_collected) || 0;
    if (manualHst > 0) return sum + manualHst;
    // Fallback: since sold_price is HST-exclusive, calculate HST at 13% on top
    const saleAmount = (Number(v.sold_price) || 0) - (Number(v.trade_in_value) || 0);
    return sum + parseFloat((saleAmount * 0.13).toFixed(2));
  }, 0);

  // ITCs on vehicle purchases are claimed in the period they were purchased, not sold
  const itcVehiclePurchases = vehicles.reduce((sum, v) => {
    if (v.purchase_hst && (!getDateRange() || (v.purchase_date && isInPeriod(v.purchase_date)))) {
      return sum + (Number(v.purchase_hst) || 0);
    }
    return sum;
  }, 0);

  const itcVehicleExpenses = filteredVehicleExpenses.reduce((sum, e) => sum + Number(e.hst_amount), 0);
  const itcBusinessExpenses = filteredBusinessExpenses.reduce((sum, e) => sum + Number(e.hst_amount), 0);
  const totalItcs = itcVehiclePurchases + itcVehicleExpenses + itcBusinessExpenses;
  const netHstOwing = parseFloat((hstCollectedTotal - totalItcs).toFixed(2));

  // Profit calculations - sold_price is pre-HST (HST-exclusive)
  const totalRevenue = soldVehicles.reduce((sum, v) => {
    return sum + (Number(v.sold_price) || 0);
  }, 0);

  const totalVehicleCost = soldVehicles.reduce((sum, v) => {
    const purchaseCost = Number(v.purchase_cost) || 0;
    const vehicleExpenseTotal = vehicleExpenses
      .filter((e) => e.vehicle_id === v.id)
      .reduce((s, e) => s + Number(e.amount), 0);
    return sum + purchaseCost + vehicleExpenseTotal;
  }, 0);

  const totalBusinessExp = filteredBusinessExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const grossProfit = totalRevenue - totalVehicleCost;
  const netProfit = grossProfit - totalBusinessExp;

  // Period label for display
  const getPeriodLabel = () => {
    const range = getDateRange();
    switch (period) {
      case "month": return "This Month";
      case "last_month": return "Last Month";
      case "quarter": return "This Quarter";
      case "last_quarter": return "Last Quarter";
      case "year": return "This Year";
      case "last_year": return "Last Year";
      case "custom":
        if (range) {
          return `${range.start.toLocaleDateString("en-CA")} — ${range.end.toLocaleDateString("en-CA")}`;
        }
        return "Custom Range";
      default: return "All Time";
    }
  };

  const handleExportHST = async () => {
    try {
      const periodLabel = getPeriodLabel();

      const hstData = [
        { Item: "HST Collected (from vehicle sales)", Amount: hstCollectedTotal.toFixed(2) },
        { Item: "Less: ITCs - Vehicle Purchases", Amount: `-${itcVehiclePurchases.toFixed(2)}` },
        { Item: "Less: ITCs - Vehicle Expenses", Amount: `-${itcVehicleExpenses.toFixed(2)}` },
        { Item: "Less: ITCs - Business Expenses", Amount: `-${itcBusinessExpenses.toFixed(2)}` },
        { Item: "", Amount: "" },
        { Item: "Net HST Owing to CRA", Amount: netHstOwing.toFixed(2) },
      ];

      const soldData = soldVehicles.map((v) => {
        const expensesForVehicle = vehicleExpenses.filter((e) => e.vehicle_id === v.id);
        const totalExp = expensesForVehicle.reduce((s, e) => s + Number(e.amount), 0);
        const soldPrice = Number(v.sold_price) || 0;
        const manualHst = Number(v.hst_collected) || 0;
        const saleAmount = soldPrice - (Number(v.trade_in_value) || 0);
        const hst = manualHst > 0 ? manualHst : parseFloat((saleAmount * 0.13).toFixed(2));
        const profit = soldPrice - (Number(v.purchase_cost) || 0) - totalExp;
        return {
          Vehicle: `${v.year} ${v.make} ${v.model}`,
          VIN: v.vin,
          "Purchase Cost": (Number(v.purchase_cost) || 0).toFixed(2),
          "Purchase HST": (Number(v.purchase_hst) || 0).toFixed(2),
          "Expenses": totalExp.toFixed(2),
          "Sale Price": soldPrice.toFixed(2),
          "Trade-in": (Number(v.trade_in_value) || 0).toFixed(2),
          "HST Collected": hst.toFixed(2),
          "Revenue (excl HST)": soldPrice.toFixed(2),
          "Net Profit": profit.toFixed(2),
          "Sold Date": v.sold_date || "",
        };
      });

      const vExpData = filteredVehicleExpenses.map((e) => {
        const v = vehicles.find((vh) => vh.id === e.vehicle_id);
        return {
          Vehicle: v ? `${v.year} ${v.make} ${v.model}` : "Unknown",
          VIN: v?.vin || "",
          Date: new Date(e.expense_date + "T00:00:00").toLocaleDateString("en-CA"),
          Category: e.category,
          Description: e.description || "",
          "Amount (excl HST)": Number(e.amount).toFixed(2),
          "HST Paid": Number(e.hst_amount).toFixed(2),
          Receipt: e.receipt_filename || "",
        };
      });

      const bExpData = filteredBusinessExpenses.map((e) => ({
        Date: new Date(e.expense_date + "T00:00:00").toLocaleDateString("en-CA"),
        Category: e.category,
        Description: e.description || "",
        "Amount (excl HST)": Number(e.amount).toFixed(2),
        "HST Paid": Number(e.hst_amount).toFixed(2),
        Receipt: e.receipt_filename || "",
      }));

      await exportToExcel(
        [
          { name: "HST Summary", data: hstData },
          { name: "Sold Vehicles", data: soldData },
          { name: "Vehicle Expenses", data: vExpData },
          { name: "Business Expenses", data: bExpData },
        ],
        `FinancialReport_${periodLabel.replace(/\s/g, "")}_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Financial report exported");
    } catch (error) {
      console.error("Failed to export HST report:", error);
      toast.error("Failed to export financial report. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const periodLabel = getPeriodLabel();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Financial Dashboard</h1>
          <p className="text-sm text-muted-foreground">HST remittance & CRA reporting</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="flex-1 sm:w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportHST} className="flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Custom Date Range Picker */}
      {period === "custom" && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1">Start Date</Label>
                <Input
                  type="date"
                  value={customStart}
                  max={customEnd || new Date().toISOString().split("T")[0]}
                  onChange={(e) => handleCustomStartChange(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1">End Date</Label>
                <Input
                  type="date"
                  value={customEnd}
                  min={customStart}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => handleCustomEndChange(e.target.value)}
                />
              </div>
              {customStart && customEnd && (
                <p className="text-xs text-muted-foreground pb-2">
                  Showing data from {new Date(customStart + "T00:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })} to {new Date(customEnd + "T00:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* HST Remittance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" /> HST Remittance Summary — {periodLabel}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 text-sm sm:text-base">
              <span>HST Collected (from sales)</span>
              <span className="font-semibold">${hstCollectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="border-t pt-2 space-y-2 text-muted-foreground text-sm">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2"><Car className="h-4 w-4 hidden sm:block" /> ITCs: Purchases</span>
                <span>−${itcVehiclePurchases.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2"><Car className="h-4 w-4 hidden sm:block" /> ITCs: Vehicle Exp.</span>
                <span>−${itcVehicleExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2"><Building2 className="h-4 w-4 hidden sm:block" /> ITCs: Business Exp.</span>
                <span>−${itcBusinessExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className={`border-t-2 pt-3 flex justify-between items-center text-base sm:text-lg font-bold ${netHstOwing >= 0 ? "text-amber-700" : "text-green-700"}`}>
              <span>{netHstOwing >= 0 ? "Net HST Owing" : "HST Credit"}</span>
              <span>{netHstOwing >= 0 ? "" : "−"}${Math.abs(netHstOwing).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profit Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5" /> Profit Summary — {periodLabel}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Vehicles Sold</p>
              <p className="text-xl sm:text-2xl font-bold">{soldVehicles.length}</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="text-xl sm:text-2xl font-bold">${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Vehicle Costs</p>
              <p className="text-xl sm:text-2xl font-bold">${totalVehicleCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Biz. Expenses</p>
              <p className="text-xl sm:text-2xl font-bold">${totalBusinessExp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
            </div>
            <div className={`text-center p-3 rounded-lg col-span-2 sm:col-span-1 ${netProfit >= 0 ? "bg-green-100" : "bg-red-100"}`}>
              <p className="text-xs text-muted-foreground">Net Profit</p>
              <p className={`text-xl sm:text-2xl font-bold ${netProfit >= 0 ? "text-green-700" : "text-red-700"}`}>
                {netProfit >= 0 ? "" : "−"}${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Sold Vehicles */}
      {soldVehicles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sold Vehicles — {periodLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mobile: Card layout */}
            <div className="sm:hidden space-y-3">
              {soldVehicles.map((v) => {
                const expForVehicle = vehicleExpenses.filter((e) => e.vehicle_id === v.id);
                const totalExp = expForVehicle.reduce((s, e) => s + Number(e.amount), 0);
                const soldPrice = Number(v.sold_price) || 0;
                const manualHst = Number(v.hst_collected) || 0;
                const saleAmount = soldPrice - (Number(v.trade_in_value) || 0);
                const revenue = manualHst > 0 ? soldPrice : calculateNetPrice(saleAmount);
                const profit = revenue - (Number(v.purchase_cost) || 0) - totalExp;
                return (
                  <div key={v.id} className="border rounded-lg p-3 space-y-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{v.year} {v.make} {v.model}</p>
                        <p className="text-xs text-muted-foreground font-mono">{v.vin}</p>
                      </div>
                      <p className={`font-bold ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>
                        {profit >= 0 ? "+" : "−"}${Math.abs(profit).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Cost: ${((Number(v.purchase_cost) || 0) + totalExp).toLocaleString()}</span>
                      <span>Sold: ${(Number(v.sold_price) || 0).toLocaleString()}</span>
                      <span>{v.sold_date || ""}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3 font-medium">Vehicle</th>
                    <th className="text-right p-3 font-medium">Purchase</th>
                    <th className="text-right p-3 font-medium">Expenses</th>
                    <th className="text-right p-3 font-medium">Sale Price</th>
                    <th className="text-right p-3 font-medium">Profit</th>
                    <th className="text-left p-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {soldVehicles.map((v) => {
                    const expForVehicle = vehicleExpenses.filter((e) => e.vehicle_id === v.id);
                    const totalExp = expForVehicle.reduce((s, e) => s + Number(e.amount), 0);
                    const soldPrice = Number(v.sold_price) || 0;
                    const manualHst = Number(v.hst_collected) || 0;
                    const saleAmount = soldPrice - (Number(v.trade_in_value) || 0);
                    const revenue = manualHst > 0 ? soldPrice : calculateNetPrice(saleAmount);
                    const profit = revenue - (Number(v.purchase_cost) || 0) - totalExp;
                    return (
                      <tr key={v.id} className="hover:bg-muted/50">
                        <td className="p-3">
                          <div>{v.year} {v.make} {v.model}</div>
                          <div className="text-xs text-muted-foreground font-mono">{v.vin}</div>
                        </td>
                        <td className="p-3 text-right">${(Number(v.purchase_cost) || 0).toLocaleString()}</td>
                        <td className="p-3 text-right">${totalExp.toLocaleString()}</td>
                        <td className="p-3 text-right">${(Number(v.sold_price) || 0).toLocaleString()}</td>
                        <td className={`p-3 text-right font-semibold ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>
                          {profit >= 0 ? "" : "−"}${Math.abs(profit).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </td>
                        <td className="p-3 text-muted-foreground">{v.sold_date || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
